import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { ExportButton } from '../../../shared/components/ExportButton';
import { PaginationSection } from '../../../shared/components/PaginationSection';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useEmailCampaigns } from '../hooks/useEmailCampaigns';
import { CreateCampaignModal } from '../components/CreateCampaignModal';
import { CampaignLogsModal } from '../components/CampaignLogsModal';
import type { EmailCampaign } from '../types';

const PAGE_SIZE = 20;

/**
 * TCS-admin news & offer email campaigns (PE-2125, BFF PE-2124, backend PE-891).
 *
 * Scope is deliberately the buildable half — list, send-now, delivery logs and
 * both exports. Scheduling, segmentation and open-rate stats have no backend to
 * read from and are tracked in PE-2204; see `../types.ts` for the full contract
 * note. Better a small honest surface than controls that 502.
 */
export function EmailCampaignsPage() {
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(1);
  const [composing, setComposing] = useState(false);
  const [viewing, setViewing] = useState<EmailCampaign | null>(null);

  const { data, isLoading, error } = useEmailCampaigns(page, PAGE_SIZE);
  const campaigns = data?.data ?? [];

  // Mirrors the BFF: EMAIL_CAMPAIGN.CREATE is required on the POST and watchman
  // grants it to COLLECTIVE_ADMIN but NOT COLLECTIVE_SUPERADMIN (which is
  // read-only across commerce resources). Showing the button to a superadmin
  // would hand them a guaranteed 403.
  const canCreate = hasPermission(Permission.CAMPAIGN_CREATE);

  return (
    <div className="py-6 max-w-7xl mx-auto px-2">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Email campaigns</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            News &amp; offer emails sent to a merchant's customers. Campaigns send immediately.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButton endpoint="/email-campaigns/export" filenameBase="email-campaigns" />
          {canCreate && <Button onClick={() => setComposing(true)}>New campaign</Button>}
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        {isLoading ? (
          <p className="text-[14px] text-muted-foreground py-12 text-center">Loading campaigns…</p>
        ) : error ? (
          <p className="text-[14px] text-red-500 py-12 text-center">Could not load campaigns.</p>
        ) : campaigns.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[14px] text-foreground">No campaigns yet.</p>
            <p className="text-[13px] text-muted-foreground mt-1">
              {canCreate
                ? 'Send your first news & offer email to a merchant’s customers.'
                : 'Campaigns sent by TCS admins will appear here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 font-[600] text-foreground">Title</th>
                  <th className="px-4 py-3 font-[600] text-foreground">Solution</th>
                  <th className="px-4 py-3 font-[600] text-foreground">Template</th>
                  <th className="px-4 py-3 font-[600] text-foreground">Recipients</th>
                  <th className="px-4 py-3 font-[600] text-foreground">Sent</th>
                  <th className="px-4 py-3 font-[600] text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="border-t border-border">
                    <td className="px-4 py-3 text-foreground">{campaign.title}</td>
                    <td className="px-4 py-3 text-muted-foreground">{campaign.solutionId}</td>
                    <td className="px-4 py-3 text-muted-foreground">{campaign.templateId}</td>
                    <td className="px-4 py-3 text-foreground tabular-nums">
                      {campaign.recipientCount}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {new Date(campaign.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" onClick={() => setViewing(campaign)}>
                        View delivery
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(data?.total ?? 0) > PAGE_SIZE && (
        <PaginationSection
          currentPage={page}
          totalItems={data?.total ?? 0}
          itemsPerPage={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      <CreateCampaignModal isOpen={composing} onClose={() => setComposing(false)} />
      <CampaignLogsModal campaign={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
